import { Button, ConditionalRendering, Modal, Radio } from "@marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAutorenew,
  MdCode,
  MdOutlineDashboard,
  MdOutlineRouter
} from "react-icons/md";
import { TbArrowBigRight } from "react-icons/tb";

import type {
  AddProductModalState,
  AddProductModalStep,
  ProductCategoryType,
  ProductDeliveryType
} from "../../types";
import { ProductTypeRadio } from "./product-type-radio";

type AddProductModalProps = AddProductModalState & {
  onClose: () => void;
};

export const AddProductModal = ({ opened, onClose }: AddProductModalProps) => {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation("common");
  const { t: tProduct } = useTranslation("product");
  const [step, setStep] = useState<AddProductModalStep>("delivery");
  const [selectedType, setSelectedType] =
    useState<ProductDeliveryType>("manual");
  const [selectedCategory, setSelectedCategory] =
    useState<ProductCategoryType>("api");

  // Reset state when modal opened
  useEffect(() => {
    if (opened) {
      setStep("category");
      setSelectedCategory("api");
      setSelectedType("manual");
    }
  }, [opened]);

  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    onClose();
    navigate({
      to: "/products/list/create",
      search: {
        category: selectedCategory
      }
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={tProduct("modal.addProductModal.title")}
      size={430}
    >
      <div className="mb-6">
        <ConditionalRendering condition={step === "delivery"}>
          <Radio.Group
            value={selectedType}
            onChange={setSelectedType as (value: string) => void}
          >
            <div className="flex flex-col gap-y-[14px]">
              <ProductTypeRadio
                value="manual"
                title="Manual"
                description="get tokens manually"
                icon={<MdAutorenew size={40} />}
              />
              <ProductTypeRadio
                value="automatic"
                title="Automatic"
                description="get tokens automatically"
                icon={<TbArrowBigRight size={40} />}
              />
            </div>
          </Radio.Group>
        </ConditionalRendering>

        <ConditionalRendering condition={step === "category"}>
          <Radio.Group
            value={selectedCategory}
            onChange={setSelectedCategory as (value: string) => void}
          >
            <div className="flex flex-col gap-y-[14px]">
              <ProductTypeRadio
                value="api"
                title="API Product"
                description="Connect and scale with ease"
                icon={<MdCode size={40} />}
              />
              <ProductTypeRadio
                value="software"
                title="Software Product"
                description="Software that drives growth"
                icon={<MdOutlineDashboard size={40} />}
              />
              <ProductTypeRadio
                value="connectivity"
                title="Connectivity"
                description="Data and connect"
                icon={<MdOutlineRouter size={40} />}
              />
            </div>
          </Radio.Group>
        </ConditionalRendering>
      </div>
      <div className="flex justify-end items-center gap-x-[14px]">
        <Button variant="secondary" className="w-[128px]" onClick={onClose}>
          {tCommon("cancel")}
        </Button>
        <ConditionalRendering condition={step === "delivery"}>
          <Button
            variant="primary"
            className="w-[128px]"
            onClick={() => setStep("category")}
          >
            {tCommon("next")}
          </Button>
        </ConditionalRendering>
        <ConditionalRendering condition={step === "category"}>
          <Button
            variant="primary"
            className="w-[128px]"
            onClick={handleConfirm}
          >
            {tCommon("confirm")}
          </Button>
        </ConditionalRendering>
      </div>
    </Modal>
  );
};
